
import React from 'react';
import { 
    Box, Layers, Zap, PenTool, GitFork, 
    Scissors, Merge, Clock, Radio, 
    Wind, Target, Sparkles, Orbit, 
    Waves, LayoutGrid, Timer, Volume2
} from 'lucide-react';
import { SimulationConfig } from '@/types';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { CollapsibleSection } from './CollapsibleSection';
import { InfoPopup } from './InfoPopup';
import { SectionLabel } from './SectionLabel';

interface GenerationSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const SpawnerSettingsSection: React.FC<GenerationSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  const updateConfig = (key: keyof SimulationConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

    return (
    <CollapsibleSection 
        title="Spawner Studio" 
        icon={GitFork} 
        darkMode={config.darkMode} 
        settingKey="spawner"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
    >
        <div className="flex flex-col gap-8 mt-4 pb-4">
            
            {/* CORE LIMITS & TRIGGERS */}
            <div className="space-y-4">
                <SectionLabel icon="fa-bolt" darkMode={config.darkMode} settingKey="spawnerTriggers">Spawn Triggers</SectionLabel>
                <div className="grid grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Boundary Hit"
                        settingKey="spawnOnWallCollision"
                        active={config.spawnOnWallCollision}
                        onToggle={() => updateConfig('spawnOnWallCollision', !config.spawnOnWallCollision)}
                        darkMode={config.darkMode}
                        desc="Create on wall hit"
                    />
                    <ModifierCard 
                        label="Inertial Hit"
                        settingKey="spawnOnBallCollision"
                        active={config.spawnOnBallCollision}
                        onToggle={() => updateConfig('spawnOnBallCollision', !config.spawnOnBallCollision)}
                        darkMode={config.darkMode}
                        desc="Create on ball hit"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-3xl border border-zinc-950/5 bg-zinc-50/50 dark:bg-white/5">
                    <SmartInput 
                        label="Genesis Count" 
                        settingKey="initialBalls"
                        min={1} max={100} 
                        value={config.initialBalls} 
                        onChange={(v: number) => updateConfig('initialBalls', v)} 
                        darkMode={config.darkMode} 
                    />
                    <SmartInput 
                        label="Max Population" 
                        settingKey="maxBalls"
                        min={1} max={5000} 
                        value={config.maxBalls} 
                        onChange={(v: number) => updateConfig('maxBalls', v)} 
                        darkMode={config.darkMode} 
                    />
                </div>
            </div>

            {/* ENGINE MODES */}
            <div className="space-y-4">
                <SectionLabel icon="fa-sliders" darkMode={config.darkMode} settingKey="spawnerEngine">Deployment Protocol</SectionLabel>
                <div className={`grid grid-cols-3 gap-2 p-1.5 rounded-2xl border border-dashed transition-all ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-zinc-50 border-zinc-200'}`}>
                    {[
                        { id: 'BASIC', label: 'Classic' },
                        { id: 'FISSION', label: 'Split' },
                        { id: 'EMITTER', label: 'Stream' },
                        { id: 'PATTERN', label: 'Shape' },
                        { id: 'CHAOS', label: 'Random' }
                    ].map((mode) => (
                        <button
                            key={mode.id}
                            onClick={() => updateConfig('spawnerEngineMode', mode.id)}
                            className={`flex items-center justify-center py-2.5 px-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${config.spawnerEngineMode === mode.id ? (config.darkMode ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]' : 'bg-cyan-600 border-cyan-600 text-white shadow-md') : (config.darkMode ? 'text-white/20 border-zinc-100/5 hover:bg-white/5' : 'text-zinc-400 border-zinc-100 hover:bg-zinc-50')}`}
                        >
                            {mode.label}
                        </button>
                    ))}
                </div>

                {config.spawnerEngineMode === 'PATTERN' && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-5 gap-2">
                             {[
                                { id: 'BURST', label: 'Burst', icon: Sparkles },
                                { id: 'SPIRAL', label: 'Spiral', icon: Orbit },
                                { id: 'GRID', label: 'Grid', icon: LayoutGrid },
                                { id: 'WAVE', label: 'Wave', icon: Waves },
                                { id: 'RADIAL', label: 'Circle', icon: Target }
                             ].map((pattern) => {
                                 const Icon = pattern.icon;
                                 return (
                                    <button
                                        key={pattern.id}
                                        onClick={() => updateConfig('spawnerPattern', pattern.id)}
                                        className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${config.spawnerPattern === pattern.id ? (config.darkMode ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]' : 'bg-cyan-50 border-cyan-200 text-cyan-700 shadow-sm') : 'border-transparent opacity-40 hover:opacity-60'}`}
                                    >
                                        <Icon className="w-4 h-4 mb-1" />
                                        <span className="text-[7px] font-black">{pattern.label}</span>
                                    </button>
                                 );
                             })}
                        </div>
                        <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4">
                            <SmartInput 
                                label="Spawn Amount" 
                                min={1} max={50} 
                                value={config.spawnerBurstCount} 
                                onChange={(v) => updateConfig('spawnerBurstCount', v)} 
                                darkMode={config.darkMode} 
                            />
                            <SmartInput 
                                label="Inherit Speed" 
                                min={0} max={2} step={0.1}
                                value={config.spawnerVelocityInheritance} 
                                onChange={(v) => updateConfig('spawnerVelocityInheritance', v)} 
                                darkMode={config.darkMode} 
                            />
                        </div>
                    </div>
                )}

                {config.spawnerEngineMode === 'EMITTER' && (
                    <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4 animate-in fade-in slide-in-from-top-2">
                         <SmartInput 
                            label="Spawn Gap (ms)" 
                            min={10} max={5000} step={10}
                            value={config.spawnerSpawnInterval} 
                            onChange={(v) => updateConfig('spawnerSpawnInterval', v)} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Spread Angle" 
                            min={0} max={360} 
                            value={config.spawnerVelocitySpread} 
                            onChange={(v) => updateConfig('spawnerVelocitySpread', v)} 
                            darkMode={config.darkMode} 
                        />
                    </div>
                )}
            </div>

            {/* FISSION / SPLITTING */}
            <div className="space-y-4">
                <SectionLabel icon="fa-scissors" darkMode={config.darkMode} settingKey="spawnerFission">Split Settings</SectionLabel>
                <ModifierCard 
                    label="Enable Splitting"
                    settingKey="fissionEnabled"
                    active={config.fissionEnabled}
                    onToggle={() => updateConfig('fissionEnabled', !config.fissionEnabled)}
                    darkMode={config.darkMode}
                    desc="Ball breaks into smaller ones"
                />
                {config.fissionEnabled && (
                    <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4 animate-in zoom-in-95 duration-300">
                        <div className="grid grid-cols-2 gap-4">
                            <SmartInput 
                                label="Split Limit" 
                                min={1} max={10} 
                                value={config.fissionMaxDepth} 
                                onChange={(v) => updateConfig('fissionMaxDepth', v)} 
                                darkMode={config.darkMode} 
                            />
                            <SmartInput 
                                label="Pieces" 
                                min={2} max={8} 
                                value={config.fissionSplitCount} 
                                onChange={(v) => updateConfig('fissionSplitCount', v)} 
                                darkMode={config.darkMode} 
                            />
                        </div>
                        <SmartInput 
                            label="Shrink Amount" 
                            min={0.1} max={0.95} step={0.05}
                            value={config.fissionSizeMultiplier} 
                            onChange={(v) => updateConfig('fissionSizeMultiplier', v)} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Split Speed" 
                            min={0.5} max={5} step={0.1}
                            value={config.fissionVelocityBoost} 
                            onChange={(v) => updateConfig('fissionVelocityBoost', v)} 
                            darkMode={config.darkMode} 
                        />
                    </div>
                )}
            </div>

            {/* FUSION / MERGING */}
            <div className="space-y-4">
                <SectionLabel icon="fa-object-group" darkMode={config.darkMode} settingKey="spawnerFusion">Merge Settings</SectionLabel>
                <ModifierCard 
                    label="Enable Merging"
                    settingKey="fusionEnabled"
                    active={config.fusionEnabled}
                    onToggle={() => updateConfig('fusionEnabled', !config.fusionEnabled)}
                    darkMode={config.darkMode}
                    desc="Combine small balls into one"
                />
                {config.fusionEnabled && (
                    <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4 animate-in zoom-in-95">
                         <SmartInput 
                            label="Max Size" 
                            min={50} max={500} 
                            value={config.fusionMaxRadius} 
                            onChange={(v) => updateConfig('fusionMaxRadius', v)} 
                            darkMode={config.darkMode} 
                        />
                        <ModifierCard 
                            label="Slow Giants"
                            active={config.fusionMassConservation}
                            onToggle={() => updateConfig('fusionMassConservation', !config.fusionMassConservation)}
                            darkMode={config.darkMode}
                            desc="Bigger balls move slower"
                        />
                    </div>
                )}
            </div>

            {/* LIFECYCLE */}
            <div className="space-y-4">
                <SectionLabel icon="fa-clock" darkMode={config.darkMode} settingKey="spawnerLifecycle">Lifecycle</SectionLabel>
                <ModifierCard 
                    label="Auto-Remove"
                    settingKey="ballLifespanEnabled"
                    active={config.ballLifespanEnabled}
                    onToggle={() => updateConfig('ballLifespanEnabled', !config.ballLifespanEnabled)}
                    darkMode={config.darkMode}
                    desc="Balls vanish after some time"
                />
                {config.ballLifespanEnabled && (
                    <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4 animate-in zoom-in-95">
                         <SmartInput 
                            label="Lifespan (sec)" 
                            min={0.5} max={60} step={0.5}
                            value={config.ballLifespan} 
                            onChange={(v) => updateConfig('ballLifespan', v)} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Fade Speed" 
                            min={0} max={5} step={0.1}
                            value={config.ballFadeOutPower} 
                            onChange={(v) => updateConfig('ballFadeOutPower', v)} 
                            darkMode={config.darkMode} 
                        />
                        <ModifierCard 
                            label="Shrink on Death"
                            active={config.ballShrinkOnDeath}
                            onToggle={() => updateConfig('ballShrinkOnDeath', !config.ballShrinkOnDeath)}
                            darkMode={config.darkMode}
                            desc="Get smaller before vanishing"
                        />
                    </div>
                )}
            </div>

            {/* AUDIO REACTIVE */}
            <div className="space-y-4">
                <SectionLabel icon="fa-music" darkMode={config.darkMode} settingKey="spawnerSoundSync">Sound Sync</SectionLabel>
                <ModifierCard 
                    label="Beat Spawn"
                    settingKey="spawnOnAudioPeak"
                    active={config.spawnOnAudioPeak}
                    onToggle={() => updateConfig('spawnOnAudioPeak', !config.spawnOnAudioPeak)}
                    darkMode={config.darkMode}
                    desc="Spawn on drum hits and beats"
                />
                {config.spawnOnAudioPeak && (
                    <div className="p-4 rounded-3xl border border-zinc-950/5 space-y-4 animate-in zoom-in-95">
                        <SmartInput 
                            label="Sensitivity" 
                            min={0.1} max={1.0} step={0.05}
                            value={config.spawnAudioThreshold} 
                            onChange={(v) => updateConfig('spawnAudioThreshold', v)} 
                            darkMode={config.darkMode} 
                        />
                    </div>
                )}
            </div>

        </div>
    </CollapsibleSection>
  );
};

const ModifierCard = ({ label, active, onToggle, darkMode, desc, settingKey }: any) => (
    <button 
        onClick={onToggle}
        className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${active ? (darkMode ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white border-cyan-200 shadow-sm') : 'bg-white/5 border-transparent opacity-40 hover:opacity-60'}`}
    >
        <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase tracking-tight ${active && !darkMode ? 'text-cyan-700' : ''}`}>{label}</span>
                {settingKey && <div onClick={e => e.stopPropagation()}><InfoPopup settingKey={settingKey} darkMode={darkMode} /></div>}
            </div>
            <span className="text-[8px] font-bold opacity-40 uppercase">{desc}</span>
        </div>
        <Toggle active={active} onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); onToggle(); }} color={active ? (darkMode ? 'bg-cyan-500' : 'bg-cyan-600') : undefined} />
    </button>
);
